import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { fr, ar, enUS } from 'date-fns/locale';
import { addBookmark, removeBookmark } from '../../../redux/actions/newsAction';
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  useTheme,
  alpha,
} from '@mui/material';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import ShareIcon from '@mui/icons-material/Share';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useTranslation } from 'react-i18next';

function countryFlag(code) {
  return code
    .toUpperCase()
    .split('')
    .map((c) => String.fromCodePoint(0x1f1e6 + c.charCodeAt(0) - 65))
    .join('');
}

const REGION_LABELS = {
  north_africa: { en: 'North Africa', fr: 'Afrique du Nord', ar: 'شمال أفريقيا' },
  gcc: { en: 'GCC', fr: 'CCG', ar: 'مجلس التعاون الخليجي' },
  west_africa: { en: 'West Africa', fr: "Afrique de l'Ouest", ar: 'غرب أفريقيا' },
  east_africa: { en: 'East Africa', fr: "Afrique de l'Est", ar: 'شرق أفريقيا' },
  middle_east: { en: 'Middle East', fr: 'Moyen-Orient', ar: 'الشرق الأوسط' },
  europe: { en: 'Europe', fr: 'Europe', ar: 'أوروبا' },
};

const NewsCard = ({ news }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const { loading } = useSelector(state => state.legalNews);
  const isAuthenticated = useSelector(state => state.auth?.user?.user);

  const dateLocale = i18n.language === 'ar' ? ar : i18n.language === 'fr' ? fr : enUS;
  const formattedDate = news.published_at
    ? format(new Date(news.published_at), 'dd MMMM yyyy', { locale: dateLocale })
    : '';

  const handleBookmarkToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) {
      window.location.href = '/login?redirect=/legal-news';
      return;
    }
    if (news.is_bookmarked) {
      dispatch(removeBookmark(news.id));
    } else {
      dispatch(addBookmark(news.id));
    }
  };

  const handleShare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const url = window.location.origin + `/legal-news/${news.id}`;
    if (navigator.share) {
      navigator.share({ title: news.title, text: news.summary, url }).catch(console.error);
    } else {
      navigator.clipboard.writeText(url);
    }
  };

  return (
    <Box
      data-tour="news-card"
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '12px',
        overflow: 'hidden',
        mb: 1.5,
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          borderColor: alpha(theme.palette.primary.main, 0.3),
          boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.4 : 0.08)}`,
          transform: 'translateY(-1px)',
        },
      }}
    >
      <Link to={`/legal-news/${news.id}`} style={{ display: 'contents', textDecoration: 'none', color: 'inherit' }}>
        {/* Thumbnail */}
        <Box
          sx={{
            position: 'relative',
            width: { xs: '100%', md: 200 },
            minHeight: { xs: 160, md: 'auto' },
            flexShrink: 0,
            backgroundColor: theme.palette.action.hover,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          {news.image_url ? (
            <img
              src={news.image_url}
              alt={news.title}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <ArticleOutlinedIcon sx={{ fontSize: 40, color: theme.palette.text.disabled }} />
          )}
          {news.is_featured && (
            <Box
              sx={{
                position: 'absolute',
                top: 10,
                left: 10,
                px: 1,
                py: 0.25,
                borderRadius: '6px',
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                fontSize: '11px',
                fontWeight: 700,
              }}
            >
              {t('news_hub.featured')}
            </Box>
          )}
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, p: { xs: 2, md: 2.5 }, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {/* Categories */}
          {news.categories && news.categories.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.25 }}>
              {news.categories.map((category, i) => (
                <Chip
                  key={i}
                  label={t(`news_category.${category}`)}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: '11px',
                    fontWeight: 500,
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    color: theme.palette.primary.main,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    borderRadius: '6px',
                    '& .MuiChip-label': { px: 1 },
                  }}
                />
              ))}
            </Box>
          )}

          {/* Title */}
          <Typography
            variant="h6"
            sx={{
              fontSize: { xs: '0.975rem', md: '1.05rem' },
              fontWeight: 600,
              color: theme.palette.text.primary,
              lineHeight: 1.4,
              mb: 1,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              transition: 'color 0.15s ease',
              '.MuiBox-root:hover &': {
                color: theme.palette.primary.main,
              },
            }}
          >
            {news.title}
          </Typography>

          {/* Summary */}
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              lineHeight: 1.6,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              flex: 1,
              mb: 2,
            }}
          >
            {news.summary}
          </Typography>

          {/* Footer */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              pt: 1.5,
              borderTop: `1px solid ${theme.palette.divider}`,
              mt: 'auto',
            }}
          >
            <Typography variant="caption" sx={{ color: theme.palette.text.disabled, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <span style={{ fontWeight: 500, color: theme.palette.text.secondary }}>
                {news.source_name || 'Lawis News'}
              </span>
              {' · '}{formattedDate}
              {news.country ? (
                <>
                  <span style={{ opacity: 0.5 }}>·</span>
                  <span>{countryFlag(news.country)}</span>
                </>
              ) : news.region && REGION_LABELS[news.region] ? (
                <>
                  <span style={{ opacity: 0.5 }}>·</span>
                  <span>{REGION_LABELS[news.region][i18n.language] || REGION_LABELS[news.region].en}</span>
                </>
              ) : null}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {isAuthenticated && (
                <Tooltip title={news.is_bookmarked ? t('news_hub.bookmarks.remove') : t('news_hub.bookmarks.add')}>
                  <IconButton
                    data-tour="news-bookmark"
                    onClick={handleBookmarkToggle}
                    disabled={loading.addBookmark || loading.removeBookmark}
                    size="small"
                    sx={{
                      color: news.is_bookmarked ? theme.palette.primary.main : theme.palette.text.disabled,
                      '&:hover': {
                        color: theme.palette.primary.main,
                        backgroundColor: alpha(theme.palette.primary.main, 0.08),
                      },
                    }}
                  >
                    {loading.addBookmark || loading.removeBookmark ? (
                      <CircularProgress size={16} />
                    ) : news.is_bookmarked ? (
                      <BookmarkIcon sx={{ fontSize: 18 }} />
                    ) : (
                      <BookmarkBorderIcon sx={{ fontSize: 18 }} />
                    )}
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip title={t('share')}>
                <IconButton
                  onClick={handleShare}
                  size="small"
                  sx={{
                    color: theme.palette.text.disabled,
                    '&:hover': {
                      color: theme.palette.text.secondary,
                      backgroundColor: theme.palette.action.hover,
                    },
                  }}
                >
                  <ShareIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>

              <Box
                component={Link}
                to={`/legal-news/${news.id}`}
                onClick={(e) => e.stopPropagation()}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1.25,
                  py: 0.5,
                  borderRadius: '6px',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: theme.palette.primary.main,
                  textDecoration: 'none',
                  transition: 'all 0.15s ease',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                {t('view_article')}
                <ArrowForwardIcon sx={{ fontSize: 14 }} />
              </Box>
            </Box>
          </Box>
        </Box>
      </Link>
    </Box>
  );
};

export default NewsCard;
